import clsx from 'clsx';
import React from 'react';
import styles from './BulletSeparatedList.module.scss';

type BulletSeparatedListAllowedChildren = React.ReactElement<
  React.ComponentProps<typeof BulletSeparatedListItem>
>;

interface BulletSeparatedListProps {
  items:
    | BulletSeparatedListAllowedChildren
    | BulletSeparatedListAllowedChildren[]
    | (BulletSeparatedListAllowedChildren | BulletSeparatedListAllowedChildren[])[];
  separator?: React.ReactNode;
  className?: string;
  testId?: string;
}

export const BulletSeparatedList: React.FC<BulletSeparatedListProps> = ({
  items,
  separator = '•',
  className,
  testId,
}) => {
  let itemsArray: React.ReactNode[];

  // Check if items is a React.Fragment and extract its children if so
  if (React.isValidElement(items) && items.type === React.Fragment) {
    itemsArray = React.Children.toArray(items.props.children);
  } else {
    itemsArray = Array.isArray(items) ? items : [items];
  }

  // Add separator between items
  const interspersedItems = itemsArray.reduce<React.ReactNode[]>((acc, item, index) => {
    if (index > 0) {
      acc.push(
        <span className={styles.separator} key={`separator-${index}`} aria-hidden="true">
          {separator}
        </span>,
      );
    }
    acc.push(item);
    return acc;
  }, []);

  return (
    <div role="list" className={clsx(styles.list, className)} data-testid={testId}>
      {interspersedItems}
    </div>
  );
};

interface BulletSeparatedListItemProps {
  children: React.ReactNode;
  className?: string;
}

export const BulletSeparatedListItem: React.FC<BulletSeparatedListItemProps> = ({
  children,
  className,
}) => (
  <span role="listitem" className={clsx(styles.item, className)}>
    {children}
  </span>
);
